import * as React from "react";
import { useTranslation } from "react-i18next";

import { useFormField } from "@/components/ui/form";
import { cn } from "@/lib/utils";

/**
 * Translated form message — Phase 6a.8.
 *
 * Drop-in replacement for shadcn's <FormMessage /> that resolves the
 * underlying error message as an i18n key via react-i18next. The Zod
 * schema in this feature emits message strings shaped like
 * `"error.required"` / `"error.invalidDate"` etc., and this wrapper
 * translates them on every render so language-toggle updates the visible
 * error text instantly.
 *
 * Lives in the feature folder rather than `components/ui/` because we
 * keep the auto-generated shadcn primitives unmodified per spec rules.
 */
export const FormMessageT = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement>
>(({ className, children, ...props }, ref) => {
  const { error, formMessageId } = useFormField();
  const { t } = useTranslation();

  const rawMessage = error?.message ? String(error.message) : null;
  // Cast around the strict i18n key union: Zod emits dynamic strings whose
  // membership in the resource keyspace can't be statically proven.
  const body: React.ReactNode = rawMessage
    ? (t(rawMessage as never) as string)
    : children;

  if (!body) return null;

  return (
    <p
      ref={ref}
      id={formMessageId}
      className={cn("text-sm font-medium text-destructive", className)}
      {...props}
    >
      {body}
    </p>
  );
});
FormMessageT.displayName = "FormMessageT";
